import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { I18nService, TranslationModule } from 'i18n';
import { Media, MediaGridComponent, MediaService, MediaViewerOverlayService } from 'media';
import { Observable, combineLatest, of, switchMap, take } from 'rxjs';

/**
 * Course content viewer (Wolfpack Assets). Reuses the media lib primitives:
 * MediaService.loadMedia(lang, 'courses', <path>) lists the chapters as thumbnail
 * cards (MediaGridComponent) and MediaViewerOverlayService opens a chapter PDF in
 * the shared full-screen viewer. Access is enforced server-side by the media
 * framework — an un-entitled user gets a 403 and is bounced to the catalog.
 */
@Component({
  selector: 'app-course-viewer-page',
  standalone: true,
  imports: [CommonModule, TranslationModule, MediaGridComponent],
  templateUrl: './course-viewer-page.component.html',
  styleUrls: ['./course-viewer-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CourseViewerPageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private mediaService = inject(MediaService);
  private overlay = inject(MediaViewerOverlayService);
  private i18n = inject(I18nService);
  private destroyRef = inject(DestroyRef);

  chapters = signal<Media[]>([]);
  loading = signal(true);
  error = signal(false);
  courseRef = signal('');
  // Current media path (dot-joined for nesting); starts at the course folder.
  private path = signal('');

  // media-grid requires a mediaOf mapper; our entities already are Media.
  mediaOf = (m: Media): Observable<Media | null> => of(m);

  constructor() {
    // The shared media overlay can leave a minimized "Conference: …" status bar
    // appended to the app body (a leftover of its call/minimize feature, which this
    // app never uses). Clear any stray ones when entering a course.
    this.clearStatusBars();

    combineLatest([this.route.paramMap, this.i18n.currentLanguage$])
      .pipe(
        switchMap(([params, lang]) => {
          const ref = params.get('courseRef') || '';
          this.courseRef.set(ref);
          this.path.set(ref);
          return this.fetch(lang, ref);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.handler());
  }

  private fetch(lang: string, path: string) {
    this.loading.set(true);
    this.error.set(false);
    return this.mediaService.loadMedia(lang, 'courses', path);
  }

  private handler() {
    return {
      next: (items: Media[]) => {
        this.chapters.set(items || []);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(true);
        this.loading.set(false);
      }
    };
  }

  onSelect(media: Media): void {
    if (Media.isFolder(media)) {
      const next = `${this.path()}.${media.id}`;
      this.path.set(next);
      this.fetch(this.i18n.getLanguage() || 'en', next)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(this.handler());
    } else {
      this.openViewer(media);
    }
  }

  // Open the PDF viewer overlay and make the browser Back button close it (instead
  // of leaving the course page). We push a throwaway history entry on open; Back
  // pops it -> we close the overlay. When it is closed via the close button, we pop
  // the entry ourselves so the page's own Back keeps working. If the user actually
  // navigates away while it is open, the overlay disposes on navigation and we must
  // NOT fight that with history.back() — router.getCurrentNavigation() detects it.
  private openViewer(media: Media): void {
    this.overlay.open({ media }).then(ref => {
      history.pushState({ mediaViewer: true }, '');
      let closedByBack = false;
      let done = false;

      // destroy() fully disposes the overlay; close() only MINIMIZES it to a
      // status bar (the "Conference: …" strip). Back should fully close.
      const onPop = () => { closedByBack = true; ref.destroy(); };
      window.addEventListener('popstate', onPop);

      const cleanup = () => {
        if (done) return;
        done = true;
        window.removeEventListener('popstate', onPop);
        this.clearStatusBars();
        const navigating = !!this.router.getCurrentNavigation();
        if (!closedByBack && !navigating && history.state && history.state.mediaViewer) {
          history.back();
        }
      };

      ref.onClosed.pipe(take(1)).subscribe(cleanup);
      ref.onDestroyed.pipe(take(1)).subscribe(cleanup);
    });
  }

  back(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate([`/${lang}/program-catalog`]);
  }

  // Remove any leftover minimized media-overlay status bars from the DOM. Safe in
  // this app: it has no conference/call feature, so no legitimate status bar exists.
  private clearStatusBars(): void {
    document.querySelectorAll('.statusbar').forEach(el => el.remove());
  }
}
